import CONSTANT, { CACHE } from '../utils/constants';
import { FLOW } from '../../../common/src/constant';
import { getDeferredCallbackResponseForEvent as originalDeferredCallackHandler } from '../utils/deferred-utils';
import { ifSuccessOrThrowError, getApi as originalGetApi } from '../utils/api-response-utils';
import {
  getKey,
  equalsIgnoreCase,
  pick,
  find,
  findIndex,
  updatePayloadBasedOnStrategy
} from '../utils/common';

const _ERROR_MESSAGES = {
  NO_EMI_BANKS: 'No banks available for EMI',
  NO_EMI_PLANS: 'EMI option is not available on this bank',
  NO_INITIATE_CHECKOUT_FOR_DEFAULT: 'Initiate checkout is not supported for default flow',
  EMI_AND_CARD_BANK_MISMATCH: 'The card does not belong to the selected bank.',
  NO_EMI_PLANS_FOR_MODE: 'EMI option is not available for provided card type.'
};

const _CARD_LABELS = {
  [CONSTANT.CC]: 'Credit Card',
  [CONSTANT.DC]: 'Debit Card'
};

const _EMI_TYPES_DISPLAY_NAMES = {
  [CONSTANT.EMI_SUBVENTION]: 'Zero/Low Cost EMI',
  [CONSTANT.EMI_STANDARD]: 'Standard  EMI'
};

/* -------------------------------------------------------------------------- */
/*                            Common / helper methods                         */
/* -------------------------------------------------------------------------- */

function _sortByBankName(banks) {
  return banks.sort((a, b) => {
    if (a.bankName < b.bankName) {
      return -1;
    }
    if (a.bankName > b.bankName) {
      return 1;
    }
    return 0;
  });
}

function _validateCardWithEmi(bankDetails = {}) {
  const cardDetails = find(bankDetails.payment_intent || [], data =>
    Object.prototype.hasOwnProperty.call(data, 'banks')
  );

  if (cardDetails) {
    const cardBank = getKey(cardDetails, 'banks', [])[0] || '';
    const cardType = getKey(cardDetails, 'mode_type');

    if (
      !(
        equalsIgnoreCase(bankDetails.bankCode, cardBank) ||
        equalsIgnoreCase(bankDetails.bankName, cardBank)
      )
    ) {
      return _ERROR_MESSAGES.EMI_AND_CARD_BANK_MISMATCH;
    }
    if (!equalsIgnoreCase(bankDetails.cardType, cardType)) {
      return _ERROR_MESSAGES.NO_EMI_PLANS_FOR_MODE;
    }
  }

  return null;
}

function _getTxnAmountFromCache(cache) {
  return cache.getData(CONSTANT.TXN_AMOUNT);
}

function _formatBankList(bankList) {
  const mappedBankList = (bankList || []).map(bank => {
    const emiType = bank.type;
    let cardTypes = [];

    for (const card of bank.cardTypes || []) {
      const { cardLabel, cardType } = card;
      const bankDetails = (card.bankDetails || []).map(bankDetail => ({
        ...bankDetail,
        cardLabel,
        cardType,
        emiType
      }));

      if (bankDetails.length) {
        cardTypes = cardTypes.concat(bankDetails);
      }
    }

    return {
      ...bank,
      displayLabel: _EMI_TYPES_DISPLAY_NAMES[emiType] || emiType,
      cardTypes: _sortByBankName(cardTypes)
    };
  });

  // append subvention bank at first
  const subventionBanksIndex = findIndex(
    mappedBankList,
    bank => CONSTANT.EMI_SUBVENTION === bank.type
  );
  if (subventionBanksIndex !== -1) {
    mappedBankList.unshift(mappedBankList.splice(subventionBanksIndex, 1)[0]);
  }

  return mappedBankList;
}

function _getDisplayName(channel) {
  return `${channel.channelName} ${_CARD_LABELS[channel.emiType]}`;
}
/* -------------------------------------------------------------------------- */
/*                              Deferred Flow EMI                             */
/* -------------------------------------------------------------------------- */

const _deferredFlowEmiApi = (function deferredFlowEMIAPIS() {
  function createApi(cache) {
    this.getDeferredCallbackResponseForEvent = originalDeferredCallackHandler.bind(this, cache);
    this.cache = cache;
  }

  /* -------------------------------- Bank list ------------------------------- */

  createApi.prototype.getBankList = function getBankList() {
    return this.getDeferredCallbackResponseForEvent(
      CONSTANT.DEFERRED_EVENTS.FETCH_EMI_BANK_DETAILS
    ).then(response => {
      const bankList = getKey(response, 'emi_bank_details');
      return _formatBankList(bankList);
    });
  };

  /* -------------------------------- EMI Plans ------------------------------- */

  function _isHybridPayModeDisabled(emiPlansResponse, bankCode) {
    const paymentModes = getKey(
      emiPlansResponse,
      'payment_instruments.body.merchantPayOption.paymentModes',
      []
    );
    const emiPayMode = find(paymentModes, mode => mode.paymentMode === CONSTANT.EMI);
    const selectedEmiBank =
      emiPayMode &&
      find(emiPayMode.payChannelOptions || [], channel => channel.channelCode === bankCode);

    return Boolean(selectedEmiBank && selectedEmiBank.isHybridDisabled);
  }

  function _parseEmiPlan(bankCode, response) {
    const emiPlans = getKey(response, 'emi_plans', null);

    if (!emiPlans) {
      throw new Error(_ERROR_MESSAGES.NO_EMI_PLANS);
    }

    const formattedEmiPlan = pick(emiPlans, [
      'bankName',
      'bankCode',
      'cardType',
      'bankLogoUrl',
      'details'
    ]);

    formattedEmiPlan.details = (formattedEmiPlan.details || []).map(detail =>
      pick(detail, [
        'planId',
        'interval',
        'emi',
        'effectivePrice',
        'gratifications',
        'rate',
        'pgPlanId'
      ])
    );

    return {
      bankCode,
      cart: getKey(response, 'cart', null),
      emiPlans: formattedEmiPlan,
      isHybridDisabled: _isHybridPayModeDisabled(response, bankCode),
      payableAmount: getKey(response, 'paymentInfo.payableAmount.value', 0)
    };
  }

  createApi.prototype.getEmiPlans = function getEmiPlans(payload) {
    const cardError = _validateCardWithEmi(payload);

    if (cardError) {
      return Promise.reject(new Error(cardError));
    }

    return this.getDeferredCallbackResponseForEvent(
      CONSTANT.DEFERRED_EVENTS.FETCH_EMI_PLANS,
      payload
    ).then(response => _parseEmiPlan(payload.bankCode, response));
  };

  /* -------------------------------- Apply EMI ------------------------------- */

  createApi.prototype.applyEmi = function applyEmi(payload) {
    return this.getDeferredCallbackResponseForEvent(
      CONSTANT.DEFERRED_EVENTS.APPLY_EMI,
      payload
    ).then(response => {
      const fallBackAmount = _getTxnAmountFromCache(this.cache);
      const hasEmiPlans = Boolean(getKey(response, 'emi_plan', null));
      response.emi_plans = response.emi_plan;

      return {
        apiResponse: _parseEmiPlan(payload.bankCode, response),
        hasEmiPlans,
        fallBackAmount,
        txnAmount: getKey(response, 'paymentInfo.payableAmount.value', fallBackAmount)
      };
    });
  };

  /* ---------------------------- Initiate Checkout --------------------------- */

  createApi.prototype.initiateCheckout = function initiateCheckout(payload) {
    return this.getDeferredCallbackResponseForEvent(CONSTANT.DEFERRED_EVENTS.INITIATE_TXN, payload);
  };

  /* ------------------------------ Deferred APIs ----------------------------- */

  return createApi;
})();

/* -------------------------------------------------------------------------- */
/*                              Default Flow EMI                              */
/* -------------------------------------------------------------------------- */

const _defaultFlowEmiApi = (function defaultFlowEmiApi() {
  function createApi(cache) {
    this.getApi = originalGetApi.bind(this, cache);
    this.cache = cache;
  }

  /* -------------------------------- Bank list ------------------------------- */

  createApi.prototype.getBankList = function getBankList() {
    const paymentOptions = this.cache.getData(CACHE.PAYMENT_OPTIONS, {});
    const paymentModes = getKey(paymentOptions, 'merchantPayOption.paymentModes', []);
    const emiPayMode = find(paymentModes, mode => mode.paymentMode === CONSTANT.EMI);
    const emiPayChannels = getKey(emiPayMode || {}, 'data.banks', []);
    const iconBaseUrl = this.cache.getData(CONSTANT.ICON_BASE_URL);

    if (!emiPayChannels.length) {
      return Promise.reject(new Error(_ERROR_MESSAGES.NO_EMI_BANKS));
    }
    const cardTypes = emiPayChannels.map(channel => ({
      bankCode: channel.channelCode,
      bankName: channel.channelName,
      cardType: channel.emiType,
      emiType: CONSTANT.EMI_STANDARD,
      cardLabel: _CARD_LABELS[channel.emiType],
      bankLogoUrl: iconBaseUrl + channel.iconUrl,
      isHybridDisabled: channel.isHybridDisabled,
      displayName: _getDisplayName(channel)
    }));
    const bankList = [
      {
        cardTypes: _sortByBankName(cardTypes),
        info: [],
        label: _EMI_TYPES_DISPLAY_NAMES[CONSTANT.EMI_STANDARD],
        type: CONSTANT.EMI_STANDARD,
        displayLabel: _EMI_TYPES_DISPLAY_NAMES[CONSTANT.EMI_STANDARD]
      }
    ];
    return Promise.resolve(bankList);
  };

  /* -------------------------------- EMI plans ------------------------------- */

  function _parseEmiPlan(bankDetails, apiResponse, cache) {
    const emiDetail = getKey(apiResponse, 'emiDetail');

    if (!emiDetail) {
      throw new Error(_ERROR_MESSAGES.NO_EMI_PLANS);
    }
    const emiChannelInfos = getKey(emiDetail, 'emiChannelInfos', []);

    const formattedEmiDetails = emiChannelInfos.map(detail => {
      const { planId, ofMonths, emiAmount = {}, totalAmount = {}, interestRate } = detail;

      return {
        planId,
        interval: ofMonths,
        emi: emiAmount.value,
        effectivePrice: totalAmount.value,
        rate: interestRate,
        pgPlanId: planId,
        gratifications: []
      };
    });

    const emiPlans = {
      ...pick(bankDetails, ['bankName', 'bankCode', 'cardType', 'bankLogoUrl']),
      details: formattedEmiDetails
    };

    return {
      bankCode: bankDetails.bankCode,
      cart: null,
      emiPlans,
      isHybridDisabled: emiDetail.isHybridDisabled,
      payableAmount: _getTxnAmountFromCache(cache)
    };
  }

  createApi.prototype.getEmiPlans = function getEmiPlans(payload) {
    const cardError = _validateCardWithEmi(payload);

    if (cardError) {
      return Promise.reject(new Error(cardError));
    }

    return this.getApi()
      .emiDetails(payload.bankCode, _getTxnAmountFromCache(this.cache), payload.cardType)
      .then(response =>
        ifSuccessOrThrowError(response, callBackResponse =>
          _parseEmiPlan(payload, callBackResponse, this.cache)
        )
      );
  };

  /* ------------------------------ Apply EMI ------------------------------ */

  createApi.prototype.applyEmi = function applyEmi() {
    const txnAmount = _getTxnAmountFromCache(this.cache);

    return Promise.resolve({
      apiResponse: null,
      hasEmiPlans: true,
      fallBackAmount: txnAmount,
      txnAmount
    });
  };

  /* ------------------------------ Default APIs ------------------------------ */

  return createApi;
})();

/* -------------------------------------------------------------------------- */
/*                              Default Flow EMI SUBVENTION                   */
/* -------------------------------------------------------------------------- */

const _defaultFlowEmiSubventionApi = (function defaultFlowEmiSubventionApi() {
  function createApi(cache) {
    this.getApi = originalGetApi.bind(this, cache);
    this.cache = cache;
  }

  /* -------------------------------- Bank list EMI SUBVENTION  ------------------------------- */

  createApi.prototype.getBankList = function getBankList(payload) {
    return this.getApi()
      .emiSubventionBanks(payload)
      .then(response => {
        const status = getKey(response, 'body.resultInfo.resultStatus', 'F');
        const resultCode = getKey(response, 'body.resultInfo.resultCode', '');
        if (status === 'S') {
          const bankList = getKey(response, 'body.emiTypes');
          return _formatBankList(bankList);
        }
        /* session expired handling */
        if (resultCode === '1006') {
          const mConfig = this.cache.getData(CACHE.MERCHANT_CONFIG);
          this.cache.getData(CACHE.STORE).setState({ sessionExpired: true });
          const notifyMerchant = getKey(mConfig, 'handler.notifyMerchant', undefined);
          if (notifyMerchant && notifyMerchant.constructor === Function) {
            notifyMerchant(CONSTANT.NOTIFY_MERCHANT_EVENTS.SESSION_EXPIRED, response);
          }
        }
        throw new Error(getKey(response, 'body.resultInfo.resultMsg', ''));
      });
  };

  /* -------------------------------- EMI plans  EMI SUBVENTION  ------------------------------- */

  function _parseEmiPlan(bankCode, response, cache) {
    const planDetails = getKey(response, 'planDetails', null);

    if (!planDetails) {
      throw new Error(_ERROR_MESSAGES.NO_EMI_PLANS);
    }

    const formattedEmiPlan = pick(response, [
      'bankName',
      'bankCode',
      'cardType',
      'bankLogoUrl',
      'planDetails'
    ]);

    formattedEmiPlan.details = (formattedEmiPlan.planDetails || []).map(detail => {
      return pick(detail, [
        'planId',
        'interval',
        'emi',
        'effectivePrice',
        'gratifications',
        'rate',
        'pgPlanId',
        'finalTransactionAmount',
        'itemBreakUp',
        'interest'
      ]);
    });

    delete formattedEmiPlan.planDetails;
    return {
      bankCode,
      emiPlans: formattedEmiPlan,
      payableAmount: _getTxnAmountFromCache(cache)
    };
  }

  createApi.prototype.getEmiPlans = function getEmiPlans(payload) {
    const cardError = _validateCardWithEmi(payload);
    if (cardError) {
      return Promise.reject(new Error(cardError));
    }

    const obj = updatePayloadBasedOnStrategy(this.cache, {}, payload.subventionObject);
    return this.getApi()
      .emiSubventionPlans(payload.bankCode, payload.cardType, obj)
      .then(response =>
        ifSuccessOrThrowError(response, callBackResponse =>
          _parseEmiPlan(payload, callBackResponse, this.cache)
        )
      );
  };

  /* ------------------------------ Apply EMI  EMI SUBVENTION  ------------------------------ */

  createApi.prototype.applyEmi = function applyEmi(payload) {
    const fallBackAmount = this.cache.getData(CONSTANT.FALLBACK_TXN_AMOUNT);
    const obj = updatePayloadBasedOnStrategy(this.cache, {}, payload);
    return this.getApi()
      .emiSubventionValidatePlan(payload, obj)
      .then(response => {
        if (getKey(response, 'body.resultInfo.resultStatus', 'F') === 'S') {
          return {
            apiResponse: response.body,
            hasEmiPlans: true,
            fallBackAmount,
            txnAmount: getKey(response, 'body.finalTransactionAmount', fallBackAmount)
          };
        }

        /* session expired handling */
        if (getKey(response, 'body.resultInfo.resultCode', '') === '1006') {
          const mConfig = this.cache.getData(CACHE.MERCHANT_CONFIG);
          this.cache.getData(CACHE.STORE).setState({ sessionExpired: true });
          const notifyMerchant = getKey(mConfig, 'handler.notifyMerchant', undefined);
          if (notifyMerchant && notifyMerchant.constructor === Function) {
            notifyMerchant(CONSTANT.NOTIFY_MERCHANT_EVENTS.SESSION_EXPIRED, response);
          }
        }
        throw new Error(
          getKey(response, 'body.resultInfo.resultMsg', 'Selected Plan is not offered by bank')
        );
      });
  };

  /* ------------------------------ Default APIs ------------------------------ */

  return createApi;
})();

/* -------------------------------------------------------------------------- */
/*                              EMI API Evaluation                              */
/* -------------------------------------------------------------------------- */

/* setting a value to call default flow validate emi in case of access token flow because for
standard emi plans in access token flow there is no support at backend and on validate it throws
error */
export function emiApi(cache, callDefaultFlowInAccessToken = false) {
  const { flow } = cache.getData(CACHE.MERCHANT_CONFIG);
  const isEmiSubventionFlow = cache.getData(CONSTANT.IS_EMISUBVENTION_ACCESS_TOKEN_FLOW);

  /* if flow is deferred execute deferred emi api
   * if flow is default and emi subvention key is true then execute the
   * _defaultFlowEmiSubventionApi functions */

  const Api = equalsIgnoreCase(flow, FLOW.DEFERRED)
    ? _deferredFlowEmiApi
    : isEmiSubventionFlow
    ? callDefaultFlowInAccessToken
      ? _defaultFlowEmiApi
      : _defaultFlowEmiSubventionApi
    : _defaultFlowEmiApi;

  return new Api(cache);
}



// WEBPACK FOOTER //
// ./controllers/emi.js